'use client';

import { useEffect, useState } from 'react';
import { ArrowRight, Info, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '@/app/hooks/context/AuthContext';
import { useFeatures } from '@/app/hooks/useFeatures';
import { CoinIcon, SokoCoinIcon } from '@/app/components/SvgIcons';
import { getExchangeStatus, performExchange } from '@/app/api/exchange/api';
import { ExchangeDirection, ExchangeStatus } from '@/types/exchange';

interface ExchangeModalProps {
    onClose: () => void;
}

const DEFAULT_RATE = 1;
const DEFAULT_MIN_IN = 100;

const toastStyle = { background: '#1f2937', color: '#fff', borderRadius: '12px' };

export default function ExchangeModal({ onClose }: ExchangeModalProps) {
    const { user, updateBalances } = useAuth();
    const { isEnabled } = useFeatures();
    const [direction, setDirection] = useState<ExchangeDirection>('points_to_soko');
    const [amount, setAmount] = useState(DEFAULT_MIN_IN);
    const [status, setStatus] = useState<ExchangeStatus | null>(null);
    const [statusLoading, setStatusLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = 'unset'; };
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await getExchangeStatus();
                if (!cancelled) setStatus(res);
            } catch (e) {
                if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load exchange status.');
            } finally {
                if (!cancelled) setStatusLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const points = user?.points ?? 0;
    const sokoPoints = user?.soko_points ?? 0;
    const rate = status?.rate ?? DEFAULT_RATE;
    const minIn = status?.min_in ?? DEFAULT_MIN_IN;
    const sourceBalance = direction === 'points_to_soko' ? points : sokoPoints;
    const targetBalance = direction === 'points_to_soko' ? sokoPoints : points;
    const received = Math.floor(amount / rate);
    const fromLabel = direction === 'points_to_soko' ? 'Kick Points' : 'Soko Points';
    const toLabel = direction === 'points_to_soko' ? 'Soko Points' : 'Kick Points';

    // Feature flags per direction — each tab reflects its own flag, and the
    // currently selected direction's flag gates the form itself.
    const pointsToSokoEnabled = isEnabled('exchange_points_to_soko');
    const sokoToPointsEnabled = isEnabled('exchange_soko_to_points');
    const directionEnabled = direction === 'points_to_soko' ? pointsToSokoEnabled : sokoToPointsEnabled;

    const clampAmount = (v: number) => {
        if (!Number.isFinite(v)) return minIn;
        return Math.min(maxFill, Math.max(minIn, Math.round(v)));
    };

    // Only cap left is the balance itself — there is no per-exchange maximum.
    const maxFill = Math.max(minIn, Math.floor(sourceBalance));

    const selectDirection = (next: ExchangeDirection) => {
        if (next === direction || submitting) return;
        setDirection(next);
        setAmount(minIn);
        setError(null);
    };

    const isValid = amount >= minIn && amount <= sourceBalance;

    const handleSubmit = async () => {
        if (!status?.available || submitting || !isValid || !directionEnabled) return;

        setSubmitting(true);
        setError(null);
        try {
            const res = await performExchange(direction, amount);
            updateBalances(res.points, res.soko_points);
            toast.success(`Exchanged ${res.spent.toLocaleString()} for ${res.received.toLocaleString()}!`, {
                style: toastStyle,
                iconTheme: { primary: '#10b981', secondary: '#1f2937' },
            });
            setStatus((prev) => (prev ? { ...prev, available: false, next_available: null } : prev));
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Exchange failed.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[150] flex items-start sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto"
            onClick={onClose}
        >
            <div
                className="bg-gray-900 shadow-2xl rounded-2xl w-full max-w-md my-4 sm:my-8 overflow-hidden max-h-[calc(100dvh-2rem)] sm:max-h-[85vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="relative bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 p-6 shrink-0">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
                    >
                        <span className="text-2xl font-bold leading-none">×</span>
                    </button>
                    <h2 className="text-2xl font-bold text-white">Exchange</h2>
                    <p className="text-green-100 text-sm mt-1">
                        Convert between Kick Points and Soko Points at a {rate}:1 rate.
                    </p>
                </div>

                {/* Direction tabs */}
                <div className="flex border-b border-gray-800 shrink-0">
                    {([
                        { dir: 'points_to_soko' as ExchangeDirection, enabled: pointsToSokoEnabled, icon: <SokoCoinIcon size={16} />, label: 'To Soko Points' },
                        { dir: 'soko_to_points' as ExchangeDirection, enabled: sokoToPointsEnabled, icon: <CoinIcon size={16} />, label: 'To Kick Points' },
                    ]).map((tab) => {
                        const active = direction === tab.dir;
                        return (
                            <button
                                key={tab.dir}
                                type="button"
                                onClick={() => selectDirection(tab.dir)}
                                disabled={submitting || !tab.enabled}
                                className={`flex-1 py-3 px-4 flex flex-col items-center justify-center gap-0.5 text-xs sm:text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-gray-400 ${
                                    active
                                        ? 'text-green-400 border-b-2 border-green-400'
                                        : 'text-gray-400 hover:text-gray-300'
                                }`}
                            >
                                <span className="flex items-center gap-2">
                                    {tab.icon}
                                    {tab.label}
                                </span>
                                {!tab.enabled && (
                                    <span className="text-xs text-gray-400 font-normal">Temporarily unavailable</span>
                                )}
                            </button>
                        );
                    })}
                </div>

                <div className="p-6 space-y-5 overflow-y-auto">
                    <div className="bg-gray-800/50 rounded-xl p-3 space-y-1">
                        <div className="flex items-center gap-1.5 text-gray-300 text-xs font-semibold">
                            <Info className="w-3.5 h-3.5 text-emerald-400" />
                            Exchange rules
                        </div>
                        <ul className="text-xs text-gray-400 space-y-0.5 list-disc list-inside leading-relaxed">
                            <li>Rate: {rate} Kick Point{rate === 1 ? '' : 's'} = 1 Soko Point</li>
                            <li>Min per exchange: {minIn.toLocaleString()} — no upper limit</li>
                            <li>Limited to 1 exchange per day</li>
                        </ul>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className={`flex-1 bg-gray-800/50 rounded-xl p-3 text-center ${!directionEnabled ? 'opacity-60' : ''}`}>
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">From</p>
                            <p className={`font-bold ${directionEnabled ? 'text-gray-200' : 'text-red-400'}`}>{fromLabel}</p>
                            <p className="text-xs text-gray-400 mt-0.5"><span className="font-mono tabular-nums">{sourceBalance.toLocaleString()}</span> available</p>
                        </div>
                        <div
                            aria-hidden="true"
                            className="shrink-0 w-10 h-10 rounded-full bg-gray-800/50 flex items-center justify-center text-emerald-400"
                        >
                            <ArrowRight className="w-4 h-4" />
                        </div>
                        <div className={`flex-1 bg-gray-800/50 rounded-xl p-3 text-center ${!directionEnabled ? 'opacity-60' : ''}`}>
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">To</p>
                            <p className={`font-bold ${directionEnabled ? 'text-gray-200' : 'text-red-400'}`}>{toLabel}</p>
                            <p className="text-xs text-gray-400 mt-0.5"><span className="font-mono tabular-nums">{targetBalance.toLocaleString()}</span> available</p>
                        </div>
                    </div>

                    {!directionEnabled && (
                        <div className="text-red-300 text-xs bg-red-950/30 rounded-lg px-3 py-2 text-center">
                            {fromLabel} → {toLabel} exchange is currently disabled.
                        </div>
                    )}

                    <div>
                        <label className={`block text-sm font-semibold mb-1.5 ${directionEnabled ? 'text-green-300' : 'text-red-400'}`}>
                            Amount to exchange
                        </label>
                        <div className="relative">
                            <input
                                type="number"
                                min={minIn}
                                step={1}
                                max={maxFill}
                                value={amount}
                                disabled={submitting || !directionEnabled}
                                onChange={(e) => setAmount(clampAmount(Number(e.target.value)))}
                                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 pr-16 text-gray-200 font-mono tabular-nums focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                            <button
                                type="button"
                                onClick={() => setAmount(maxFill)}
                                disabled={submitting || !directionEnabled}
                                title="Fill with your full balance"
                                className="absolute right-2 top-1/2 -translate-y-1/2 px-2.5 py-1 rounded-md text-xs font-bold tracking-wide text-emerald-400 bg-gray-700/60 hover:bg-gray-700 hover:text-emerald-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                MAX
                            </button>
                        </div>
                        <div className="flex justify-between text-xs text-gray-400 mt-1.5">
                            <span>Min <span className="font-mono tabular-nums">{minIn.toLocaleString()}</span></span>
                            <span>Max <span className="font-mono tabular-nums">{maxFill.toLocaleString()}</span></span>
                        </div>
                    </div>

                    <div className="bg-gray-800/50 rounded-xl p-4">
                        <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3">
                            <div className="flex items-center gap-1.5 text-gray-300 min-w-0 w-full sm:w-auto sm:flex-1 justify-center sm:justify-start">
                                <span className="font-mono font-bold tabular-nums truncate">{amount.toLocaleString()}</span>
                                {fromLabel === 'Soko Points' ? <SokoCoinIcon size={14} /> : <CoinIcon size={14} />}
                                <span className="text-xs text-gray-400 truncate">{fromLabel}</span>
                            </div>
                            <ArrowRight className="w-4 h-4 text-gray-500 shrink-0 rotate-90 sm:rotate-0" />
                            <div className="flex items-center gap-1.5 text-emerald-400 min-w-0 w-full sm:w-auto sm:flex-1 justify-center sm:justify-end">
                                <span className="font-mono font-bold tabular-nums truncate">{received.toLocaleString()}</span>
                                {toLabel === 'Soko Points' ? <SokoCoinIcon size={14} /> : <CoinIcon size={14} />}
                                <span className="text-xs text-gray-400 truncate">{toLabel}</span>
                            </div>
                        </div>
                    </div>

                    {!statusLoading && status && !status.available && (
                        <div className="text-amber-300 text-xs bg-amber-500/10 rounded-lg px-3 py-2 text-center">
                            You&apos;ve already exchanged today.
                            {status.next_available ? ` Next available ${new Date(status.next_available).toLocaleString()}.` : ''}
                        </div>
                    )}

                    {amount > sourceBalance && (
                        <div className="text-red-300 text-xs bg-red-950/30 rounded-lg px-3 py-2 text-center">
                            Insufficient {fromLabel}.
                        </div>
                    )}

                    {error && (
                        <div className="text-red-300 text-xs bg-red-950/30 rounded-lg px-3 py-2 text-center">
                            {error}
                        </div>
                    )}

                    <button
                        onClick={handleSubmit}
                        disabled={submitting || statusLoading || !status?.available || !isValid || !directionEnabled}
                        className="w-full py-3 rounded-xl font-bold text-sm text-white bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 hover:shadow-emerald-500/20 hover:shadow-lg transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        {submitting ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Exchanging...
                            </>
                        ) : (
                            'Exchange'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
